import React, { useState, useEffect } from 'react';

import { dataGridPremiumColumns } from '../../utils/helper';
import { DataGridIdentifiers, DatagridPremiumUi } from '../Interface';
import itemService from '../../service/item.service';
import { ItemGroupTypeList, StockArticleTypeList } from '../../config/itemGroup';
import useGenerateJokerReportCols from '../../hooks/useGenerateJokerReportCols';

export default function ItemReports({
  refetch,
  values,
  viewField,
  operatorsDisabled,
  rowSelection,
  autocompleteOptions,
  hiddenItemGroupIds,
  jokerFields,
}) {
  const { refetchTable, setRefetchTable } = refetch;
  const { setSelectedValues } = values;
  const { rowSelectionModel, setRowSelectionModel } = rowSelection;

  const [reports, setReports] = useState({ rows: [], rowCount: 0 });

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'itemCode', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  useEffect(() => {
    if (operatorsDisabled) {
      setRowSelectionModel([]);
    }
  }, [operatorsDisabled]);

  const getList = (gridState) => {
    const allowedItemGroupIds = autocompleteOptions.itemGroup.map((itemGroup) => itemGroup.value);

    if (hiddenItemGroupIds?.length > 0 && allowedItemGroupIds.length === 0) {
      setReports({ rows: [], rowCount: 0 });
      return;
    }

    const stateWithHiddenItemGroupFilter =
      hiddenItemGroupIds?.length > 0
        ? {
            ...gridState,
            filter: {
              ...gridState.filter,
              filterModel: {
                ...gridState.filter?.filterModel,
                items: [
                  ...(gridState.filter?.filterModel?.items || []),
                  { field: 'itemGroupId', value: allowedItemGroupIds, operator: 'isAnyOf' },
                ],
                logicOperator: 'and',
              },
            },
          }
        : gridState;

    itemService.getItems(stateWithHiddenItemGroupFilter).then((data) => {
      setReports(data);
    });
  };

  const jokerCols = useGenerateJokerReportCols(jokerFields);

  const columns = dataGridPremiumColumns([
    {
      field: 'itemCode',
      headerName: 'Cikkszám',
      type: 'string',
    },
    {
      field: 'itemName',
      headerName: 'Cikknév',
      type: 'string',
    },
    {
      field: 'itemGroupId',
      type: 'singleSelect',
      headerName: 'Cikkcsoport',
      renderCell: (data) => {
        return data.row.itemGroup?.name;
      },
      valueOptions: autocompleteOptions.itemGroup,
    },
    {
      field: 'quantityUnitId',
      type: 'singleSelect',
      headerName: 'Mennyiségi egység',
      renderCell: (data) => {
        return data.row.quantityUnit?.name;
      },
      valueOptions: autocompleteOptions.quantityUnit,
    },
    {
      field: 'type',
      headerName: 'Típus',
      type: 'singleSelect',
      valueOptions: ItemGroupTypeList,
    },
    {
      field: 'stockItemType',
      headerName: 'Készletcikk fajta',
      type: 'singleSelect',
      valueOptions: StockArticleTypeList,
    },
    {
      field: 'inPurchase',
      headerName: 'Beszerzésben részt vesz',
      type: 'boolean',
    },
    {
      field: 'inSales',
      headerName: 'Értékesítésben részt vesz',
      type: 'boolean',
    },
    {
      field: 'weight',
      headerName: 'Súly (kg)',
      type: 'number',
    },
    {
      field: 'kshClassificationId',
      type: 'singleSelect',
      headerName: 'KSH besorolás',
      renderCell: (data) => {
        return data.row.kshClassification?.name;
      },
      valueOptions: autocompleteOptions.kshClassification,
    },
    {
      field: 'other',
      headerName: 'Egyéb',
      type: 'string',
    },
    {
      field: 'active',
      headerName: 'Aktív',
      type: 'boolean',
    },
    {
      field: 'barCode',
      headerName: 'Vonalkód',
      type: 'string',
    },
    {
      field: 'landedCost',
      headerName: 'Mellékköltség',
      type: 'boolean',
    },
    {
      field: 'purchaseVatCode',
      headerName: 'Beszerzés ÁFA kód',
      type: 'singleSelect',
      renderCell: (data) => {
        return data.row.purchaseVat?.code ? `${data.row.purchaseVat?.code} - ${data.row.purchaseVat?.name}` : '';
      },
      valueOptions: autocompleteOptions.purchaseVatCode,
    },
    {
      field: 'salesVatCode',
      headerName: 'Értékesítés ÁFA kód',
      type: 'singleSelect',
      renderCell: (data) => {
        return data.row.salesVat?.code ? `${data.row.salesVat?.code} - ${data.row.salesVat?.name}` : '';
      },
      valueOptions: autocompleteOptions.salesVatCode,
    },
    {
      field: 'drawingNumber',
      headerName: 'Rajzszám',
      type: 'string',
    },
    {
      field: 'defSupplier',
      headerName: 'Alapértelmezett szállító',
      type: 'string',
      renderCell: (data) => {
        return data.row.supplier?.name;
      },
    },
    ...jokerCols,
  ]);

  return (
    <DatagridPremiumUi
      datagridIdentifier={DataGridIdentifiers.item}
      datagridInitialState={initialState}
      dataLoader={getList}
      rows={reports.rows}
      refetch={{ refetchTable, setRefetchTable }}
      rowCount={reports.rowCount}
      columns={[...columns]}
      onRowClick={(rowData) => {
        if (!viewField) return;
        setSelectedValues(rowData.row);
      }}
      getRowId={(row) => row.itemCode}
      paginationAndfilterMode="server"
      onRowSelectionModelChange={(newRowSelectionModel) => {
        setRowSelectionModel(newRowSelectionModel);
      }}
      rowSelectionModel={rowSelectionModel}
      disableRowSelectionOnClick={!viewField}
    />
  );
}
